import { Injectable } from '@angular/core';
import { FilingFormState } from './filing-form-state.service';
import { SHEET_KEYS } from '../../models/sheet-keys';

/**
 * SheetDependencyService
 *
 * Single responsibility: knows which sheets depend on which for cross-sheet
 * validation.  Given a sheet that just changed, returns the set of dependent
 * sheets whose cached validation status is now potentially stale.
 *
 * The dependency map is derived from static analysis of each role component's
 * cross-sheet reads (CrossReferenceService, getSheetData, equality validations).
 *
 * This service is intentionally stateless — it holds only the static
 * dependency graph and exposes pure lookup functions.
 */
@Injectable({ providedIn: 'root' })
export class SheetDependencyService {

  /**
   * Static map: sheetKey → set of sheet keys that READ FROM it for validation.
   *
   * When the key sheet is submitted, every sheet in the value set should have
   * its cached validation status invalidated because their cross-sheet
   * validation may now produce a different result.
   *
   * Dependency sources (from codebase analysis):
   * ─────────────────────────────────────────────
   * Role-0  (filingPrerequisites)       → ALL sheets (central config hub)
   * Role-2  (scopeOfFiling)             → 5,10,11,12,13,14,15,16,17,18,20 (consolidation scope)
   * Role-4  (directorsReport)           → 7  (bidirectional data sharing)
   * Role-7  (auditorsReport)            → 2,4 (bidirectional + scope dependency)
   * Role-8  (SOFP)                      → 18,19 (cross-sheet equity + cash equivalents validation)
   * Role-9  (sub-classification)        → 8  (data inheritance / linked fields)
   * Role-12 (SOPL)                      → 18 (Role-18 reads ProfitLossBeforeTax for reconciliation)
   * Role-13 (analysis of P&L)           → 12 (linked fields into Role-12)
   * Role-15 (comprehensive income)      → 19 (cross-sheet validation)
   * Role-18 (SOCF Indirect)             → 8,12 (Role-8 SOFP cross-checks CashAndCashEquivalents
   *                                              against Role-18 end-of-period; Role-12 SOPL
   *                                              cross-checks ProfitLossBeforeTax with Role-18
   *                                              starting reconciliation figure)
   * Role-19 (SOCIE)                     → 8,15 (cross-sheet equity + P&L validation)
   */
  private readonly dependencyMap: ReadonlyMap<keyof FilingFormState, ReadonlyArray<keyof FilingFormState>> = new Map([
    // Role-2: consolidation scope affects many downstream sheets
    [
      'scopeOfFiling',
      [
        SHEET_KEYS.statementByDirectors,
        SHEET_KEYS.statementOfFinancialPositionLiquidity,
        SHEET_KEYS.subClassificationLiquidity,
        SHEET_KEYS.statementOfProfitOrLoss,
        SHEET_KEYS.analysisOfProfitOrLoss,
        SHEET_KEYS.statementOfProfitOrLossByNature,
        SHEET_KEYS.statementOfComprehensiveIncome,
        SHEET_KEYS.statementOfComprehensiveIncomeBeforeTax,
        SHEET_KEYS.statementOfCashFlowsDirectMethod,
        SHEET_KEYS.statementOfCashFlowsIndirectMethod,
        SHEET_KEYS.notesCorporateInformation,
      ]
    ],

    // Role-4 ↔ Role-7: bidirectional data sharing
    ['directorsReport', [SHEET_KEYS.auditorsReport]],
    ['auditorsReport', [SHEET_KEYS.scopeOfFiling, SHEET_KEYS.directorsReport]],

    // Role-8 -> Role-9 -> Role-19: financial position chain
    // Role-8 (SOFP) is also cross-checked by Role-18 (CashAndCashEquivalents
    // end-of-period reconciliation @ role-520000-mfrs.form.json L2007-2013), so
    // SOFP changes must invalidate Role-18 too.
    ['statementOfFinancialPosition', [SHEET_KEYS.statementOfChangesInEquity, SHEET_KEYS.statementOfCashFlowsIndirectMethod]],
    ['subClassificationOfAssetsLiabilitiesEquity', [SHEET_KEYS.statementOfFinancialPosition]],
    ['statementOfChangesInEquity', [SHEET_KEYS.statementOfFinancialPosition, SHEET_KEYS.statementOfComprehensiveIncome]],

    // Role-12 (SOPL) -> Role-18, Role-19: Role-18 reads
    // ifrs-full:ProfitLossBeforeTax for reconciliation
    // (role-520000-mfrs.form.json L38-43). Role-19 (SOCIE) declares a
    // schema-equality block against role-12's ifrs-full:ProfitLoss /
    // ifrs-smes:ProfitLoss (discovered during the role-19 Phase 2 lift.
    // SOPL changes must therefore invalidate both downstream caches.
    ['statementOfProfitOrLoss', [SHEET_KEYS.statementOfCashFlowsIndirectMethod, SHEET_KEYS.statementOfChangesInEquity]],

    // Role-13 -> Role-12: analysis feeds into main P&L statement
    ['analysisOfProfitOrLoss', [SHEET_KEYS.statementOfProfitOrLoss]],

    // Role-15 -> Role-19: comprehensive income cross-validates with SOCIE
    ['statementOfComprehensiveIncome', [SHEET_KEYS.statementOfChangesInEquity]],

    // Role-18 (SOCF Indirect) -> Role-8, Role-12: Role-8 SOFP equality rule on
    // CashAndCashEquivalents references Role-18 end-of-period total; Role-12
    // SOPL also pulls in role-18 form values via exportAllData() for its
    // cross-sheet equality pass — so Role-18 changes must invalidate both.
    ['statementOfCashFlowsIndirectMethod', [SHEET_KEYS.statementOfFinancialPosition, SHEET_KEYS.statementOfProfitOrLoss]],
  ]);

  /**
   * Get the set of sheet keys whose validation status should be invalidated
   * when the given sheet is submitted.
   *
   * Always includes the submitted sheet itself.
   * For filingPrerequisites (Role-0), returns null to signal "invalidate all".
   *
   * @param submittedSheet - The sheet key that was just submitted
   * @returns Array of sheet keys to invalidate, or null meaning "invalidate all"
   */
  getDependents(submittedSheet: keyof FilingFormState): (keyof FilingFormState)[] | null {
    // Role-0 is the central config hub — affects all sheets
    if (submittedSheet === SHEET_KEYS.filingPrerequisites) {
      return null; // Signal to caller: clear entire cache
    }

    const directDeps = this.dependencyMap.get(submittedSheet) ?? [];

    // Always include the submitted sheet itself
    const result = new Set<keyof FilingFormState>([submittedSheet, ...directDeps]);

    return Array.from(result);
  }
}
